import { Link } from 'react-router-dom';
import { useTranslation } from '../../core/hooks/use-translation';
import type { PortalNotification } from '../../core/models/portal-notification';
import { Panel } from '../../ui/panel';
import { BellIcon } from '../../ui/portal-icons';
import { describeNotification } from './describe-notification';
import { notificationLink } from './notification-link';
import { PushToggle } from './push-toggle';

interface NotificationPanelProps {
    notifications: PortalNotification[];
    unreadCount: number;
    markingRead: boolean;
    onMarkAllRead: () => void;
    onMarkRead: (id: string) => void;
}

/**
 * The notification list.
 *
 * **Opening the tab reads nothing.** A notification is read when its row is followed or its check
 * is pressed, so a glance at the list does not clear the count in the header before anything in it
 * was acted on. The explicit button also clears unread notifications outside the loaded list.
 *
 * **Unread is a bar down the side, not a tinted row.** The tint used to be the only difference
 * between read and unread, which puts the distinction entirely in a colour; the bar survives being
 * printed, dimmed, or seen by somebody who does not separate those two blues.
 *
 * **A row that names a record is the link to it**, the whole row rather than an anchor in the
 * corner — the same call the case card makes, and for the same reason: on a telephone a small
 * trailing link is a five-millimetre target under the thing the customer wants to tap. A row that
 * points nowhere stays plain text rather than becoming a link that goes to the list it is already
 * in.
 */
export function NotificationPanel({
    notifications,
    unreadCount,
    markingRead,
    onMarkAllRead,
    onMarkRead
}: NotificationPanelProps) {
    const { t, language } = useTranslation();

    return (
        <Panel
            title={t('portal.notifications')}
            icon={<BellIcon />}
            action={
                <div className="flex items-center gap-3">
                    {unreadCount > 0 && (
                        <span className="rounded-full bg-brand-600 px-2 py-0.5 text-xs font-bold text-white">
                            {unreadCount}
                        </span>
                    )}

                    {unreadCount > 0 && (
                        <button
                            type="button"
                            onClick={onMarkAllRead}
                            disabled={markingRead}
                            className="text-sm font-semibold text-brand-700 hover:underline dark:text-brand-400"
                        >
                            {t('portal.mark_all_read')}
                        </button>
                    )}
                </div>
            }
        >
            {notifications.length === 0 ? (
                <p className="text-sm text-slate-500 dark:text-slate-400">{t('portal.no_notifications')}</p>
            ) : (
                <ul className="space-y-2">
                    {notifications.map((notification) => {
                        const sentence = describeNotification(notification, language, t);
                        const target = notificationLink(notification, language);

                        return (
                            <li
                                key={notification.id}
                                className={`flex items-start gap-2 border-s-2 text-sm ${
                                    notification.read
                                        ? 'border-slate-200 text-slate-500 dark:border-slate-800 dark:text-slate-400'
                                        : 'border-brand-500 font-medium text-slate-900 dark:text-slate-100'
                                }`}
                            >
                                {target === null ? (
                                    <span className="block min-w-0 flex-1 ps-3">{sentence}</span>
                                ) : (
                                    <Link
                                        to={target}
                                        onClick={() => {
                                            if (!notification.read) onMarkRead(notification.id);
                                        }}
                                        className="block min-w-0 flex-1 rounded-e-lg py-0.5 ps-3 transition hover:bg-slate-50 hover:text-brand-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600 dark:hover:bg-slate-800/60 dark:hover:text-brand-300"
                                    >
                                        {sentence}
                                    </Link>
                                )}

                                {!notification.read && (
                                    <button
                                        type="button"
                                        onClick={() => onMarkRead(notification.id)}
                                        disabled={markingRead}
                                        aria-label={t('portal.mark_read')}
                                        title={t('portal.mark_read')}
                                        className="-my-1 inline-flex size-8 shrink-0 items-center justify-center rounded-md text-slate-400 sm:my-0 sm:size-6 transition hover:bg-slate-100 hover:text-brand-700 disabled:opacity-50 dark:hover:bg-slate-800 dark:hover:text-brand-300"
                                    >
                                        <svg
                                            className="size-3.5"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2.2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            aria-hidden="true"
                                        >
                                            <path d="m5 12 5 5 9-10" />
                                        </svg>
                                    </button>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}

            <PushToggle />
        </Panel>
    );
}
